import React, { useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BreadCrumb from '../../../components/Breadcrumbs';
import { capitalizingPhrase } from '../../../utils/format';
import { parseJwt } from '../../../utils/session';
import { AppContext } from '../../../app/AppContext';
import MenteesTable from './menteesTable';
import MentorsTable from './mentorsTable';
import SessionsTable from './SessionsTable';
import TabContainer from './tabContainer';
import SurveyTable from './surveyTable';
import ReportModal from './ReportModal';

import { Backdrop, CircularProgress } from '@material-ui/core';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import EventNoteOutlinedIcon from '@material-ui/icons/EventNoteOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import OndemandVideoOutlinedIcon from '@material-ui/icons/OndemandVideoOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import ArrowForwardOutlinedIcon from '@material-ui/icons/ArrowForwardOutlined';

import CoursesAPI from '../../CoursesContainer/course-api';
import SessionAPI from '../../SessionContainer/session-api';
import ProjectAPI from '../project-api';
import Alert from '../../../components/common/Alert';

const sanitizeFileNamePart = (value, fallback) => {
  const sanitizedValue = String(value || '')
    .trim()
    .replace(/[<>:"/\\|?*\s,-]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return sanitizedValue || fallback;
};

const getChileDateForFileName = () => {
  const dateParts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Santiago',
  })
    .formatToParts(new Date())
    .reduce((parts, part) => ({ ...parts, [part.type]: part.value }), {});

  return `${dateParts.day}_${dateParts.month}_${dateParts.year}`;
};

export const getReportFileName = (contentDisposition, fallbackFileName) => {
  if (!contentDisposition) {
    return fallbackFileName;
  }

  const encodedFileNameMatch = contentDisposition.match(
    /filename\*\s*=\s*UTF-8''([^;]+)/i
  );

  if (encodedFileNameMatch) {
    const encodedFileName = encodedFileNameMatch[1]
      .trim()
      .replace(/^"|"$/g, '');

    try {
      return decodeURIComponent(encodedFileName);
    } catch (unusedError) {
      // Continue with the regular filename when the UTF-8 value is malformed.
    }
  }

  const quotedFileNameMatch = contentDisposition.match(
    /filename\s*=\s*"([^"]+)"/i
  );
  if (quotedFileNameMatch) {
    return quotedFileNameMatch[1];
  }

  const fileNameMatch = contentDisposition.match(/filename\s*=\s*([^;\s]+)/i);
  return fileNameMatch ? fileNameMatch[1] : fallbackFileName;
};

const ProjectInfoContainer = (props) => {
  const history = useHistory();
  const { user: authenticatedUser } = useContext(AppContext);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadInProgressRef = useRef(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalProps, setModalProps] = useState({
    title: '',
    mainText: '',
    hintText: '',
    action: 'report-courses',
  });
  //routerProps
  const {
    location: { state: {
      client: client,
      announcement: announcement,
    } },
  } = props;
  const token = window.sessionStorage.getItem('converge.token.private');
  const sessionUser = parseJwt(token);
  const currentUser =
    authenticatedUser && authenticatedUser.role
      ? authenticatedUser
      : sessionUser;
  const currentRoleType =
    currentUser && currentUser.role ? currentUser.role.type : null;
  const canDownloadMeetingsReport = ['admin', 'manager'].includes(
    currentRoleType
  );

  const tabs = [
    { title: 'Mentees', Component: MenteesTable, buttonText: 'Nuevo usuario' },
    { title: 'Mentores', Component: MentorsTable, buttonText: 'Nuevo usuario' },
    {
      title: 'Reuniones',
      Component: SessionsTable,
      buttonText: 'Nueva Reunión',
    },
  ];

  const links = [
    { url: '/clientes', text: 'Clientes' },
    {
      url: '/clientes/detalles',
      text: 'Detalle del cliente',
      state: 
      { client },
    },
    { url: '/proyecto', text: 'Convocatoria' },
  ];
  const meetingsReportFallbackName = `${getChileDateForFileName()}_Reuniones_${sanitizeFileNamePart(
    client.name,
    'Sin_Cliente'
  )}_${sanitizeFileNamePart(announcement.title, 'Sin_Convocatoria')}.xlsx`;
  const registrationsReportFallbackName = `${getChileDateForFileName()}_Registros_${sanitizeFileNamePart(
    client.name,
    'Sin_Cliente'
  )}_${sanitizeFileNamePart(announcement.title, 'Sin_Convocatoria')}.xlsx`;

  const downloadReport = async ({
    getReport,
    fallbackFileName,
    successMessage,
  }) => {
    if (downloadInProgressRef.current) {
      return;
    }

    downloadInProgressRef.current = true;
    setIsDownloading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await getReport(announcement.id);

      const contentDisposition =
        response.headers && response.headers['content-disposition'];
      const fileName = getReportFileName(
        contentDisposition,
        fallbackFileName
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMessage(successMessage);
    } catch (unusedError) {
      setError(
        'Ocurrió un error al descargar el informe. Por favor, inténtalo de nuevo.'
      );
    } finally {
      downloadInProgressRef.current = false;
      setIsDownloading(false);
    }
  };

  const downloadCoursesReport = () =>
    downloadReport({
      getReport: CoursesAPI.getAnnouncementReport,
      fallbackFileName: 'Reporte_Cursos.xlsx',
      successMessage: '¡El reporte de cursos se ha descargado exitosamente!',
    });
  const downloadMeetingsReport = () =>
    downloadReport({
      getReport: SessionAPI.getAnnouncementMeetingsReport,
      fallbackFileName: meetingsReportFallbackName,
      successMessage: '¡El reporte de reuniones se ha descargado exitosamente!',
    });
  const downloadRegistrationsReport = () => {
    if (!canDownloadMeetingsReport) return;
    return downloadReport({
      getReport: ProjectAPI.getAnnouncementRegistrationsReport,
      fallbackFileName: registrationsReportFallbackName,
      successMessage: '¡El reporte de registros se ha descargado exitosamente!',
    });
  };


  return (
    <>
      <Backdrop open={isDownloading} style={{ zIndex: 1300, color: '#fff', flexDirection: 'column', gap: '16px' }}>
        <CircularProgress color="inherit" />
        <span style={{ fontSize: '16px', fontWeight: 500 }}>Generando reporte, por favor espera...</span>
      </Backdrop>

      <ReportModal 
        open={openModal} 
        announcementId={announcement.id} 
        onClose={()=> setOpenModal(false)} 
        title={modalProps.title}
        mainText={modalProps.mainText}
        hintText={modalProps.hintText}
        action={modalProps.action}
        />
      <div className="content-panel user-container">
        <BreadCrumb links={links} />
        {/* TITLE AND INFO ABOUT PROJECT */}
        {message && (
          <div className="success-message">
            <Alert type="success">{message}</Alert>
          </div>
        )}

        {error && (
          <div className="error-message">
            <Alert type="error">{error}</Alert>
          </div>
        )}
        <section className="announcement-overview" aria-labelledby="announcement-overview-title">
          <header className="announcement-overview__hero">
            <span className="announcement-overview__hero-icon" aria-hidden="true">
              <FlagOutlinedIcon />
            </span>
            <div className="announcement-overview__identity">
              <span className="announcement-overview__eyebrow">Gestión de convocatorias</span>
              <h1 id="announcement-overview-title">{capitalizingPhrase(client.name)}</h1>
              <p>{capitalizingPhrase(announcement.title)}</p>
            </div>
            <button
              type="button"
              className="announcement-overview__edit"
              onClick={() => {
                history.push('/clientes/detalles/proyecto/editar', {
                  announcement,
                  client,
                });
              }}
            >
              <EditOutlinedIcon aria-hidden="true" />
              <span>Editar convocatoria</span>
            </button>
          </header>

          <div className="announcement-overview__reports" aria-labelledby="announcement-overview-reports-title">
            <div className="announcement-overview__reports-heading">
              <h2 id="announcement-overview-reports-title">Reportes de la convocatoria</h2>
              <p>Consulta y descarga la información de sus participantes y actividades.</p>
            </div>
            <div className="announcement-overview__report-grid">
              <button
                type="button"
                className="announcement-overview__report"
                aria-label="Descargar reporte cursos"
                onClick={downloadCoursesReport}
                disabled={isDownloading}
                aria-busy={isDownloading}
              >
                <span className="announcement-overview__report-icon" aria-hidden="true">
                  <MenuBookOutlinedIcon />
                </span>
                <span className="announcement-overview__report-copy">
                  <span className="announcement-overview__report-action">Descargar reporte</span>
                  <span className="announcement-overview__report-title">Cursos</span>
                </span>
                <GetAppOutlinedIcon className="announcement-overview__report-arrow" aria-hidden="true" />
              </button>
              {canDownloadMeetingsReport && (
                <button
                  type="button"
                  className="announcement-overview__report"
                  aria-label="Descargar reporte reuniones"
                  onClick={downloadMeetingsReport}
                  disabled={isDownloading}
                  aria-busy={isDownloading}
                >
                  <span className="announcement-overview__report-icon" aria-hidden="true">
                    <EventNoteOutlinedIcon />
                  </span>
                  <span className="announcement-overview__report-copy">
                    <span className="announcement-overview__report-action">Descargar reporte</span>
                    <span className="announcement-overview__report-title">Reuniones</span>
                  </span>
                  <GetAppOutlinedIcon className="announcement-overview__report-arrow" aria-hidden="true" />
                </button>
              )}
              {canDownloadMeetingsReport && (
                <button
                  type="button"
                  className="announcement-overview__report"
                  aria-label="Descargar reporte registros"
                  onClick={downloadRegistrationsReport}
                  disabled={isDownloading}
                  aria-busy={isDownloading}
                >
                  <span className="announcement-overview__report-icon" aria-hidden="true">
                    <PeopleOutlineIcon />
                  </span>
                  <span className="announcement-overview__report-copy">
                    <span className="announcement-overview__report-action">Descargar reporte</span>
                    <span className="announcement-overview__report-title">Registros</span>
                  </span>
                  <GetAppOutlinedIcon className="announcement-overview__report-arrow" aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                className="announcement-overview__report"
                aria-label="Generar reporte de talleres"
                disabled={isDownloading}
                onClick={() => {
                  setModalProps({
                    title: 'Reporte de talleres',
                    mainText:
                      'Genera un reporte de los talleres para participantes de la convocatoria.',
                    hintText: 'El proceso puede demorar entre 30 segundos a 1 minuto.',
                    action: 'report-workshops',
                  });
                  setOpenModal(true);
                }}
              >
                <span className="announcement-overview__report-icon" aria-hidden="true">
                  <OndemandVideoOutlinedIcon />
                </span>
                <span className="announcement-overview__report-copy">
                  <span className="announcement-overview__report-action">Generar reporte de</span>
                  <span className="announcement-overview__report-title">Talleres</span>
                </span>
                <ArrowForwardOutlinedIcon className="announcement-overview__report-arrow" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>
        <TabContainer
          tabs={tabs}
          variant="bordered"
          buttonText="Nuevo usuario"
          project={{
            announcementId: announcement.id,
            title: announcement.title,
            clientId: client.id,
            client: client.name,
            clientStatus: client.status
          }}
        />
      </div>
    </>
  );
};
export default ProjectInfoContainer;
